import { Toaster as Sonner, toast as sonnerToast } from 'sonner'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import { Spinner } from './spinner'
import { useTheme } from '@/lib/theme'
import { useIsMobile } from '@/lib/hooks'
import { errorMessage } from './feedback'

/** Mounted once by every shell (and the kit). Themed with our tokens; bottom-right, top-center on mobile. */
export function Toaster() {
  const { resolved } = useTheme()
  const mobile = useIsMobile()
  return (
    <Sonner
      theme={resolved}
      position={mobile ? 'top-center' : 'bottom-right'}
      gap={8}
      visibleToasts={4}
      offset={16}
      closeButton={false}
      icons={{
        success: <CircleCheck className="size-4" />,
        error: <CircleAlert className="size-4" />,
        warning: <TriangleAlert className="size-4" />,
        info: <Info className="size-4" />,
        loading: <Spinner size={16} />,
      }}
      toastOptions={{ duration: 4000 }}
    />
  )
}

type Msg = Parameters<typeof sonnerToast>[0]
type Opts = Parameters<typeof sonnerToast>[1]

/**
 * Toasts. `toast.success('Product saved')`, `toast.error(err)` (accepts an Error/ApiError),
 * `toast.undo('3 products archived', () => restore())` (6 s with an Undo button),
 * `toast.promise(p, { loading, success, error })`.
 */
export const toast = Object.assign((m: Msg, o?: Opts) => sonnerToast(m, o), {
  success: (m: Msg, o?: Opts) => sonnerToast.success(m, o),
  info: (m: Msg, o?: Opts) => sonnerToast.info(m, o),
  warning: (m: Msg, o?: Opts) => sonnerToast.warning(m, o),
  /** Accepts a message or anything thrown (ApiError → its safe message). */
  error: (e: unknown, o?: Opts) => sonnerToast.error(typeof e === 'string' ? e : errorMessage(e), { duration: 6000, ...o }),
  /** Reversible action with an Undo button. `onUndo` may be async; failures toast an error. */
  undo: (m: Msg, onUndo: () => unknown | Promise<unknown>, o?: Opts & { undoLabel?: string }) => sonnerToast(m, {
    duration: 6000,
    ...o,
    action: {
      label: o?.undoLabel ?? 'Undo',
      onClick: () => { void Promise.resolve().then(onUndo).catch(err => sonnerToast.error(errorMessage(err))) },
    },
  }),
  promise: sonnerToast.promise,
  loading: (m: Msg, o?: Opts) => sonnerToast.loading(m, o),
  dismiss: sonnerToast.dismiss,
})
